import React, { useState, useCallback, useMemo } from "react";
import { css } from "@emotion/react";
import {
  Button,
  Heading,
} from "@phoenix/components";

// ==========================================
// Crisp SVGs for Icons
// ==========================================

export const BedrockAgentIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#ff9900"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M16.5 9.4 7.55 4.24a1.78 1.78 0 0 0-2.5 1.55v12.42a1.78 1.78 0 0 0 2.5 1.55L16.5 14.6a1.78 1.78 0 0 0 0-3.2z" />
    <path d="m21 16-4-2.5" />
    <path d="m21 8-4 2.5" />
  </svg>
);

const WifiIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12.55a11 11 0 0 1 14.08 0" />
    <path d="M1.42 9a16 16 0 0 1 21.16 0" />
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
    <line x1="12" y1="20" x2="12.01" y2="20" />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#10b981"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CrossIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#ef4444"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ChevronDownIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ShieldLockIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0f766e"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <rect x="9" y="11" width="6" height="5" rx="1" />
    <path d="M10 11V9a2 2 0 1 1 4 0v2" />
  </svg>
);

// ==========================================
// Config Interface & Defaults
// ==========================================

export interface BedrockAgentTargetConfigValues {
  // Required
  agentId: string;
  agentAliasId: string;
  region: string;

  // Authentication
  authMethod: "default" | "profile" | "explicit";
  awsProfile: string;
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;

  // Advanced Session & Conversation
  sessionId: string;
  memoryId: string;
  enableTrace: boolean;
  endSession: boolean;
  timeout: number | string;
  sessionAttributesJson: string;
  promptSessionAttributesJson: string;

  // Generic / Advanced Passthrough
  configJson: string;
  prompts: string[];
  delayMs: number | string;
  extensionHook: string;
  testGenerationInstructions: string;
}

export const DEFAULT_BEDROCK_AGENT_TARGET_CONFIG: BedrockAgentTargetConfigValues = {
  agentId: "",
  agentAliasId: "TSTALIASID",
  region: "us-east-1",
  authMethod: "default",
  awsProfile: "",
  accessKeyId: "",
  secretAccessKey: "",
  sessionToken: "",
  sessionId: "",
  memoryId: "",
  enableTrace: false,
  endSession: false,
  timeout: 120,
  sessionAttributesJson: "{}",
  promptSessionAttributesJson: "{}",
  configJson: "{}",
  prompts: ["{{prompt}}"],
  delayMs: 0,
  extensionHook: "",
  testGenerationInstructions: "",
};

export const COMMON_AWS_REGIONS = [
  { value: "us-east-1", label: "US East (N. Virginia) - us-east-1" },
  { value: "us-west-2", label: "US West (Oregon) - us-west-2" },
  { value: "eu-west-1", label: "Europe (Ireland) - eu-west-1" },
  { value: "eu-central-1", label: "Europe (Frankfurt) - eu-central-1" },
  { value: "ap-southeast-1", label: "Asia Pacific (Singapore) - ap-southeast-1" },
  { value: "ap-northeast-1", label: "Asia Pacific (Tokyo) - ap-northeast-1" },
  { value: "us-east-2", label: "US East (Ohio) - us-east-2" },
  { value: "ca-central-1", label: "Canada (Central) - ca-central-1" },
  { value: "sa-east-1", label: "South America (São Paulo) - sa-east-1" },
];

export function validateBedrockAgentTargetConfig(values: BedrockAgentTargetConfigValues): {
  isValid: boolean;
  errors: Record<string, string>;
} {
  const errors: Record<string, string> = {};

  if (!values.agentId || !values.agentId.trim()) {
    errors.agentId = "Agent ID is required (e.g. 'ABC123XYZ0').";
  }
  if (!values.agentAliasId || !values.agentAliasId.trim()) {
    errors.agentAliasId = "Agent Alias ID is required (e.g. 'TSTALIASID').";
  }
  if (!values.region || !values.region.trim()) {
    errors.region = "AWS Region is required (e.g. 'us-east-1').";
  }

  if (values.authMethod === "explicit") {
    if (!values.accessKeyId.trim()) {
      errors.accessKeyId = "Access Key ID is required when using explicit credentials.";
    }
    if (!values.secretAccessKey.trim()) {
      errors.secretAccessKey = "Secret Access Key is required when using explicit credentials.";
    }
  } else if (values.authMethod === "profile") {
    if (!values.awsProfile.trim()) {
      errors.awsProfile = "AWS Profile name is required (e.g. 'default' or SSO profile).";
    }
  }

  if (values.sessionAttributesJson && values.sessionAttributesJson.trim()) {
    try {
      JSON.parse(values.sessionAttributesJson);
    } catch {
      errors.sessionAttributesJson = "Session Attributes must be valid JSON.";
    }
  }

  if (values.promptSessionAttributesJson && values.promptSessionAttributesJson.trim()) {
    try {
      JSON.parse(values.promptSessionAttributesJson);
    } catch {
      errors.promptSessionAttributesJson = "Prompt Session Attributes must be valid JSON.";
    }
  }

  if (values.configJson && values.configJson.trim()) {
    try {
      JSON.parse(values.configJson);
    } catch {
      errors.configJson = "Advanced configuration must be valid JSON.";
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

// ==========================================
// Component Props
// ==========================================

export interface BedrockAgentTargetConfigurationProps {
  targetName: string;
  values: BedrockAgentTargetConfigValues;
  onChange: (updates: Partial<BedrockAgentTargetConfigValues>) => void;
  onBack: () => void;
  onNext: () => void;
  hideBottomBar?: boolean;
  renderModeSwitcher?: React.ReactNode;
  onTestConnection?: () => void;
  isTestingConnection?: boolean;
  testResult?: {
    success: boolean;
    message?: string;
    latency?: number | string;
  } | null;
}

// ==========================================
// Styles
// ==========================================

const containerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 900px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
`;

const cardStyle = css`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const headerRowStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f1f5f9;
`;

const providerIdPillStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 9999px;
  color: #c2410c;
  font-size: 0.82rem;
  font-family: monospace;
  font-weight: 700;
`;

const sectionHeadingStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.05rem;
  font-weight: 750;
  color: #0f172a;
  margin-bottom: 14px;
`;

const fieldGridStyle = css`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const labelStyle = css`
  display: block;
  font-size: 0.85rem;
  font-weight: 650;
  color: #334155;
  margin-bottom: 6px;
`;

const inputStyle = (isInvalid = false) => css`
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1.5px solid ${isInvalid ? "#ef4444" : "#cbd5e1"};
  border-radius: 8px;
  font-size: 0.88rem;
  color: #0f172a;
  background: #ffffff;
  outline: none;
  transition: border-color 0.15s ease;

  &:focus {
    border-color: ${isInvalid ? "#ef4444" : "#0f766e"};
    box-shadow: 0 0 0 3px ${isInvalid ? "rgba(239,68,68,0.15)" : "rgba(15,118,110,0.15)"};
  }
`;

const selectStyle = css`
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1.5px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.88rem;
  color: #0f172a;
  background: #ffffff;
  outline: none;
  cursor: pointer;

  &:focus {
    border-color: #0f766e;
    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.15);
  }
`;

const errorTextStyle = css`
  color: #ef4444;
  font-size: 0.78rem;
  margin-top: 4px;
  font-weight: 500;
`;

const helpTextStyle = css`
  color: #64748b;
  font-size: 0.80rem;
  margin-top: 4px;
`;

const authTabsRow = css`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const authTabBtn = (active: boolean) => css`
  padding: 8px 14px;
  font-size: 0.84rem;
  font-weight: ${active ? 750 : 600};
  border-radius: 8px;
  border: 1.5px solid ${active ? "#0f766e" : "#cbd5e1"};
  background: ${active ? "#f0fdfa" : "#ffffff"};
  color: ${active ? "#0f766e" : "#475569"};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #0f766e;
  }
`;

const securityNoticeBox = css`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background: #f0fdfa;
  border: 1px solid #ccfbf1;
  border-radius: 8px;
  font-size: 0.82rem;
  color: #0f766e;
  margin-top: 12px;
`;

const accordionHeaderStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  font-weight: 700;
  font-size: 0.92rem;
  color: #1e293b;

  &:hover {
    background: #f1f5f9;
  }
`;

const accordionContentStyle = css`
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-top: none;
  border-radius: 0 0 8px 8px;
  background: #ffffff;
`;

const diagnosticsCard = (success: boolean) => css`
  margin-top: 16px;
  padding: 14px 18px;
  border-radius: 8px;
  border: 1.5px solid ${success ? "#10b981" : "#ef4444"};
  background: ${success ? "#f0fdf4" : "#fef2f2"};
  color: #0f172a;
`;

export function BedrockAgentTargetConfiguration({
  targetName,
  values,
  onChange,
  onBack,
  onNext,
  hideBottomBar = false,
  renderModeSwitcher,
  onTestConnection,
  isTestingConnection = false,
  testResult = null,
}: BedrockAgentTargetConfigurationProps) {
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const { isValid, errors } = useMemo(() => validateBedrockAgentTargetConfig(values), [values]);

  const liveProviderId = useMemo(() => {
    const rawId = values.agentId.trim() || "YOUR_AGENT_ID";
    return `bedrock-agent:${rawId}`;
  }, [values.agentId]);

  const handleFieldChange = useCallback(
    (field: keyof BedrockAgentTargetConfigValues, val: any) => {
      onChange({ [field]: val });
    },
    [onChange]
  );

  return (
    <div css={containerStyle}>
      <div css={cardStyle}>
        {/* Header Row */}
        <div css={headerRowStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "10px",
                background: "#fff7ed",
                border: "1px solid #fed7aa",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <BedrockAgentIcon />
            </div>
            <div>
              <Heading level={2} style={{ margin: 0, fontSize: "1.25rem", fontWeight: 800, color: "#0f172a" }}>
                AWS Bedrock Agent Configuration
              </Heading>
              <div style={{ color: "#64748b", fontSize: "0.85rem", marginTop: "2px" }}>
                Target: <strong>{targetName || "AWS Bedrock Agent"}</strong>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span css={providerIdPillStyle} title="Generated Provider ID">
              {liveProviderId}
            </span>
            {renderModeSwitcher}
          </div>
        </div>

        {/* Section 1: Agent Identification (Required) */}
        <div style={{ marginBottom: "24px" }}>
          <div css={sectionHeadingStyle}>
            <span>1. Agent Identification (Required)</span>
          </div>

          <div css={fieldGridStyle}>
            {/* Agent ID */}
            <div>
              <label css={labelStyle}>
                Agent ID <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. ABC123XYZ0"
                value={values.agentId}
                onChange={(e) => handleFieldChange("agentId", e.target.value)}
                css={inputStyle(!!errors.agentId)}
              />
              {errors.agentId ? (
                <div css={errorTextStyle}>{errors.agentId}</div>
              ) : (
                <div css={helpTextStyle}>The alphanumeric identifier of the deployed Bedrock Agent.</div>
              )}
            </div>

            {/* Agent Alias ID */}
            <div>
              <label css={labelStyle}>
                Agent Alias ID <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. TSTALIASID"
                value={values.agentAliasId}
                onChange={(e) => handleFieldChange("agentAliasId", e.target.value)}
                css={inputStyle(!!errors.agentAliasId)}
              />
              {errors.agentAliasId ? (
                <div css={errorTextStyle}>{errors.agentAliasId}</div>
              ) : (
                <div css={helpTextStyle}>Agent routing alias. Defaults to TSTALIASID for draft test versions.</div>
              )}
            </div>
          </div>

          {/* Region */}
          <div style={{ maxWidth: "440px" }}>
            <label css={labelStyle}>
              AWS Region <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <select
              value={values.region}
              onChange={(e) => handleFieldChange("region", e.target.value)}
              css={selectStyle}
            >
              {COMMON_AWS_REGIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            {errors.region && <div css={errorTextStyle}>{errors.region}</div>}
          </div>
        </div>

        {/* Section 2: AWS Authentication */}
        <div style={{ marginBottom: "24px", paddingTop: "16px", borderTop: "1px solid #f1f5f9" }}>
          <div css={sectionHeadingStyle}>
            <span>2. AWS Authentication</span>
          </div>

          <div css={authTabsRow}>
            <button
              type="button"
              css={authTabBtn(values.authMethod === "default")}
              onClick={() => handleFieldChange("authMethod", "default")}
            >
              Default Credential Chain / IAM / Env
            </button>
            <button
              type="button"
              css={authTabBtn(values.authMethod === "profile")}
              onClick={() => handleFieldChange("authMethod", "profile")}
            >
              AWS Profile / SSO
            </button>
            <button
              type="button"
              css={authTabBtn(values.authMethod === "explicit")}
              onClick={() => handleFieldChange("authMethod", "explicit")}
            >
              Explicit Access Keys
            </button>
          </div>

          {values.authMethod === "default" && (
            <div css={securityNoticeBox}>
              <ShieldLockIcon />
              <div>
                <strong>Zero-Configuration IAM & Environment Variables</strong>
                <p style={{ margin: "2px 0 0 0" }}>
                  Evalight automatically resolves credentials via IAM role, ECS task metadata, or standard
                  environment variables (<code>AWS_ACCESS_KEY_ID</code>, <code>AWS_SECRET_ACCESS_KEY</code>, <code>AWS_SESSION_TOKEN</code>).
                </p>
              </div>
            </div>
          )}

          {values.authMethod === "profile" && (
            <div style={{ maxWidth: "440px" }}>
              <label css={labelStyle}>
                AWS Profile Name <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. default or sso-prod"
                value={values.awsProfile}
                onChange={(e) => handleFieldChange("awsProfile", e.target.value)}
                css={inputStyle(!!errors.awsProfile)}
              />
              {errors.awsProfile && <div css={errorTextStyle}>{errors.awsProfile}</div>}
              <div css={helpTextStyle}>Named profile configured in <code>~/.aws/credentials</code> or <code>~/.aws/config</code>.</div>
            </div>
          )}

          {values.authMethod === "explicit" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              <div css={fieldGridStyle}>
                <div>
                  <label css={labelStyle}>
                    AWS Access Key ID <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="AKIA..."
                    value={values.accessKeyId}
                    onChange={(e) => handleFieldChange("accessKeyId", e.target.value)}
                    css={inputStyle(!!errors.accessKeyId)}
                  />
                  {errors.accessKeyId && <div css={errorTextStyle}>{errors.accessKeyId}</div>}
                </div>

                <div>
                  <label css={labelStyle}>
                    AWS Secret Access Key <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <input
                    type="password"
                    placeholder="••••••••••••••••••••••••"
                    value={values.secretAccessKey}
                    onChange={(e) => handleFieldChange("secretAccessKey", e.target.value)}
                    css={inputStyle(!!errors.secretAccessKey)}
                  />
                  {errors.secretAccessKey && <div css={errorTextStyle}>{errors.secretAccessKey}</div>}
                </div>
              </div>

              <div style={{ maxWidth: "440px" }}>
                <label css={labelStyle}>Optional Session Token (Temporary STS Credentials)</label>
                <input
                  type="password"
                  placeholder="Optional STS session token"
                  value={values.sessionToken}
                  onChange={(e) => handleFieldChange("sessionToken", e.target.value)}
                  css={inputStyle(false)}
                />
              </div>

              <div css={securityNoticeBox}>
                <ShieldLockIcon />
                <div>
                  <strong>Secret Protection Active:</strong>
                  <span> Secret keys and tokens are securely forwarded to the execution backend and never serialized into generated YAML.</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Section 3: Advanced Conversation & Session Settings (Collapsible) */}
        <div style={{ marginBottom: "24px" }}>
          <div
            css={accordionHeaderStyle}
            onClick={() => setIsAdvancedOpen((prev) => !prev)}
            role="button"
            tabIndex={0}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span>⚙️ Advanced Configuration (Session State, Traces & Attributes)</span>
            </div>
            <ChevronDownIcon style={{ transform: isAdvancedOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
          </div>

          {isAdvancedOpen && (
            <div css={accordionContentStyle}>
              <div css={fieldGridStyle}>
                <div>
                  <label css={labelStyle}>Session ID</label>
                  <input
                    type="text"
                    placeholder="Auto-generated per probe if blank"
                    value={values.sessionId}
                    onChange={(e) => handleFieldChange("sessionId", e.target.value)}
                    css={inputStyle(false)}
                  />
                  <div css={helpTextStyle}>Custom session identifier to preserve conversation context across probes.</div>
                </div>

                <div>
                  <label css={labelStyle}>Memory ID</label>
                  <input
                    type="text"
                    placeholder="e.g. MEMORY_ID_123"
                    value={values.memoryId}
                    onChange={(e) => handleFieldChange("memoryId", e.target.value)}
                    css={inputStyle(false)}
                  />
                  <div css={helpTextStyle}>Optional memory store identifier for long-term agent memory.</div>
                </div>
              </div>

              <div css={fieldGridStyle}>
                <div>
                  <label css={labelStyle}>Request Timeout (seconds)</label>
                  <input
                    type="number"
                    min={5}
                    max={300}
                    value={values.timeout}
                    onChange={(e) => handleFieldChange("timeout", parseInt(e.target.value) || 120)}
                    css={inputStyle(false)}
                  />
                  <div css={helpTextStyle}>Maximum allowed duration for bedrock-agent-runtime response.</div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "4px" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "0.85rem", fontWeight: 650 }}>
                    <input
                      type="checkbox"
                      checked={values.enableTrace}
                      onChange={(e) => handleFieldChange("enableTrace", e.target.checked)}
                    />
                    Enable Agent Trace (Orchestration & Reasoning Steps)
                  </label>

                  <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "0.85rem", fontWeight: 650 }}>
                    <input
                      type="checkbox"
                      checked={values.endSession}
                      onChange={(e) => handleFieldChange("endSession", e.target.checked)}
                    />
                    End Session after each probe
                  </label>
                </div>
              </div>

              {/* Session Attributes JSON */}
              <div style={{ marginTop: "12px" }}>
                <label css={labelStyle}>Session Attributes (JSON)</label>
                <textarea
                  rows={2}
                  value={values.sessionAttributesJson}
                  onChange={(e) => handleFieldChange("sessionAttributesJson", e.target.value)}
                  style={{
                    width: "100%",
                    fontFamily: "monospace",
                    fontSize: "0.82rem",
                    padding: "8px 10px",
                    borderRadius: "8px",
                    border: errors.sessionAttributesJson ? "1.5px solid #ef4444" : "1px solid #cbd5e1",
                  }}
                  placeholder='{ "userRole": "admin" }'
                />
                {errors.sessionAttributesJson && <div css={errorTextStyle}>{errors.sessionAttributesJson}</div>}
              </div>

              {/* Prompt Session Attributes JSON */}
              <div style={{ marginTop: "12px" }}>
                <label css={labelStyle}>Prompt Session Attributes (JSON)</label>
                <textarea
                  rows={2}
                  value={values.promptSessionAttributesJson}
                  onChange={(e) => handleFieldChange("promptSessionAttributesJson", e.target.value)}
                  style={{
                    width: "100%",
                    fontFamily: "monospace",
                    fontSize: "0.82rem",
                    padding: "8px 10px",
                    borderRadius: "8px",
                    border: errors.promptSessionAttributesJson ? "1.5px solid #ef4444" : "1px solid #cbd5e1",
                  }}
                  placeholder='{ "context": "red-teaming-scan" }'
                />
                {errors.promptSessionAttributesJson && <div css={errorTextStyle}>{errors.promptSessionAttributesJson}</div>}
              </div>

              {/* Advanced JSON Editor */}
              <div style={{ marginTop: "12px" }}>
                <label css={labelStyle}>Additional Passthrough Configuration (JSON)</label>
                <textarea
                  rows={3}
                  value={values.configJson}
                  onChange={(e) => handleFieldChange("configJson", e.target.value)}
                  style={{
                    width: "100%",
                    fontFamily: "monospace",
                    fontSize: "0.82rem",
                    padding: "8px 10px",
                    borderRadius: "8px",
                    border: errors.configJson ? "1.5px solid #ef4444" : "1px solid #cbd5e1",
                  }}
                  placeholder='{ "additionalProperties": true }'
                />
                {errors.configJson && <div css={errorTextStyle}>{errors.configJson}</div>}
              </div>
            </div>
          )}
        </div>

        {/* Section 4: Connection Testing & Diagnostics */}
        <div style={{ paddingTop: "16px", borderTop: "1px solid #f1f5f9" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div css={sectionHeadingStyle} style={{ marginBottom: 0 }}>
                <span>4. Connection Diagnostics</span>
              </div>
              <div css={helpTextStyle}>
                Validate AWS credentials and agent accessibility by sending a test message.
              </div>
            </div>

            {onTestConnection && (
              <Button
                type="button"
                isDisabled={!isValid || isTestingConnection}
                onClick={onTestConnection}
                style={{
                  background: "#0f766e",
                  color: "#ffffff",
                  borderRadius: "8px",
                  padding: "8px 18px",
                  fontWeight: 700,
                  fontSize: "0.88rem",
                  cursor: isValid && !isTestingConnection ? "pointer" : "not-allowed",
                }}
              >
                {isTestingConnection ? "Testing Connection..." : "Test Connection"}
              </Button>
            )}
          </div>

          {testResult && (
            <div css={diagnosticsCard(testResult.success)}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 750, fontSize: "0.92rem" }}>
                {testResult.success ? <CheckIcon /> : <CrossIcon />}
                <span>{testResult.success ? "Connection Successful" : "Connection Failed"}</span>
                {testResult.latency && (
                  <span style={{ fontSize: "0.80rem", color: "#64748b", marginLeft: "auto" }}>
                    Latency: {testResult.latency}
                  </span>
                )}
              </div>
              {testResult.message && (
                <div style={{ marginTop: "6px", fontSize: "0.84rem", color: testResult.success ? "#166534" : "#991b1b" }}>
                  {testResult.message}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Bar if not hidden */}
      {!hideBottomBar && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "16px" }}>
          <Button
            variant="default"
            type="button"
            onClick={onBack}
            style={{ borderRadius: "8px", padding: "9px 20px", fontWeight: 650 }}
          >
            ← Back
          </Button>

          <Button
            type="button"
            isDisabled={!isValid}
            onClick={onNext}
            style={{
              background: "#0f766e",
              color: "#ffffff",
              borderRadius: "8px",
              padding: "9px 24px",
              fontWeight: 750,
              cursor: isValid ? "pointer" : "not-allowed",
            }}
          >
            Save & Continue →
          </Button>
        </div>
      )}
    </div>
  );
}
